import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ShoppingBag, LogOut, User, LayoutDashboard } from 'lucide-react';
import SeasonNavigation from '@/components/SeasonNavigation';
import SeasonsDownload from '@/components/SeasonsDownload';

const Landing = ({ user, onLogout }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-black border-b border-gray-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <Link to="/" className="flex items-center space-x-3">
                <img src="/hi-tec-logo.png" alt="HI-TEC" className="h-10" />
                <span className="text-white text-xl font-bold">B2B</span>
              </Link>
              
              {/* Main Catalog Buttons */}
              <div className="hidden md:flex items-center ml-8">
                <SeasonNavigation />
              </div>
            </div>
            
            <nav className="flex items-center space-x-4">
              {user ? (
                <>
                  <Link to="/my-orders">
                    <Button data-testid="nav-orders-btn" variant="ghost" className="text-white hover:text-blue-400 hover:bg-gray-800">
                      <User className="w-4 h-4 mr-2" />
                      Mis Pedidos
                    </Button>
                  </Link>
                  {(user.role === 'admin' || user.role === 'manager') && (
                    <Link to="/admin">
                      <Button data-testid="nav-admin-btn" variant="ghost" className="text-white hover:text-blue-400 hover:bg-gray-800">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Button data-testid="logout-btn" onClick={onLogout} variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                    <LogOut className="w-4 h-4 mr-2" />
                    Salir
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button data-testid="login-btn" variant="outline" className="border-white text-white hover:bg-white hover:text-black">Iniciar Sesión</Button>
                  </Link>
                  <Link to="/register">
                    <Button data-testid="register-btn" className="bg-blue-600 hover:bg-blue-700 text-white">Registrarse</Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="mb-8">
            <img src="/hi-tec-logo.png" alt="HI-TEC" className="h-24 mx-auto mb-4" />
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-black leading-tight">
            Plataforma B2B
            <span className="block mt-2 text-blue-600">
              para Distribuidores
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Accede a nuestro catálogo completo, consulta stock en tiempo real
            y realiza pedidos de forma instantánea.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <Button 
              data-testid="hero-catalog-btn"
              onClick={() => navigate('/catalog')} 
              size="lg" 
              className="px-8 py-6 text-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              📦 Ver Catálogo STOCK
            </Button>
            <Button 
              data-testid="hero-programming-aw26-btn"
              onClick={() => navigate('/programming?season=AW26')} 
              size="lg" 
              variant="outline"
              className="px-8 py-6 text-lg border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white font-semibold"
            >
              🍂 Programación AW26
            </Button>
            <Button 
              data-testid="hero-programming-ss27-btn"
              onClick={() => navigate('/programming?season=SS27')} 
              size="lg" 
              variant="outline"
              className="px-8 py-6 text-lg border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white font-semibold"
            >
              🌸 Programación SS27
            </Button>
            {!user && (
              <Button 
                data-testid="hero-register-btn"
                onClick={() => navigate('/register')} 
                size="lg" 
                variant="outline"
                className="px-8 py-6 text-lg border-2 border-black text-black hover:bg-black hover:text-white"
              >
                Crear Cuenta
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Downloads Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto mb-8">
            <SeasonsDownload user={user} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Stock en Tiempo Real</h3>
              <p className="text-gray-600">
                Consulta disponibilidad instantánea por talla y color. Stock sincronizado diariamente.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Pedidos Rápidos</h3>
              <p className="text-gray-600">
                Realiza pedidos en segundos. Recibe confirmación instantánea y seguimiento completo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Logo & Company */}
            <div>
              <img src="/hi-tec-logo.png" alt="HI-TEC" className="h-10 mb-4" />
              <p className="text-gray-400 text-sm">
                Plataforma B2B para distribuidores
              </p>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-bold mb-4">Contacto</h3>
              <div className="text-gray-300 text-sm space-y-2">
                <p className="font-semibold text-white">HI-TEC SPORTS ESPAÑA S.A.</p>
                <p>Elche Parque Empresarial</p>
                <p>Calle Antonio Gaudí, 12</p>
                <p>03203 Elche (Alicante)</p>
              </div>
            </div>

            {/* Commercial Contact */}
            <div>
              <h3 className="text-lg font-bold mb-4">Atención Comercial</h3>
              <div className="text-gray-300 text-sm space-y-2">
                <p className="font-semibold text-white">David García</p>
                <p>
                  <span className="text-gray-400">Móvil:</span>{' '}
                  <a href="tel:+34618050398" className="text-blue-400 hover:text-blue-300">
                    +34 618 050 398
                  </a>
                </p>
                <p>
                  <span className="text-gray-400">Email:</span>{' '}
                  <a href="mailto:david@gescomsport.com" className="text-blue-400 hover:text-blue-300">
                    david@gescomsport.com
                  </a>
                </p>
              </div>
              <div className="text-gray-300 text-sm space-y-2 mt-6">
                <p className="font-semibold text-white">Curro Gutiérrez</p>
                <p>
                  <span className="text-gray-400">Móvil:</span>{' '}
                  <a href="tel:+34610128693" className="text-blue-400 hover:text-blue-300">
                    +34 610 12 86 93
                  </a>
                </p>
                <p>
                  <span className="text-gray-400">Email:</span>{' '}
                  <a href="mailto:curro@gescomsport.com" className="text-blue-400 hover:text-blue-300">
                    curro@gescomsport.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 pt-6 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 HI-TEC SPORTS ESPAÑA S.A. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
